import type { App } from "vue";

import { logError } from "./logError";
import { isSessionEndedError } from "./sessionEnded";

/**
 * Last-resort error reporting for the SPA.
 *
 * A thrown error in a lifecycle hook or an unawaited promise used to vanish: no Sentry event,
 * no server log, just a page that stopped responding. These handlers make "the page died"
 * observable. They are a safety net, not a handler — nothing is recovered here, only reported.
 */

const MAX_REPORTS_PER_MINUTE = 10;
const RATE_WINDOW_MS = 60_000;

let reportTimestamps: number[] = [];
let isReporting = false;

/** Reset between tests. */
export function resetGlobalErrorReporting(): void {
    reportTimestamps = [];
    isReporting = false;
}

function underRateLimit(): boolean {
    const now = Date.now();
    reportTimestamps = reportTimestamps.filter((timestamp) => now - timestamp < RATE_WINDOW_MS);
    if (reportTimestamps.length >= MAX_REPORTS_PER_MINUTE) {
        return false;
    }
    reportTimestamps.push(now);
    return true;
}

function report(error: unknown, source: "vue_error_handler" | "window_onerror" | "unhandled_rejection", extraContext: Record<string, unknown> = {}): void {
    // Re-entrancy guard: if reporting itself throws (and so lands back here via window.onerror),
    // give up rather than loop. logError's own transport failures are already swallowed inside it.
    if (isReporting) {
        return;
    }
    // The session-ended dialog owns these; a render error unwinding from one is by design.
    if (isSessionEndedError(error)) {
        return;
    }
    // A render loop or a poller wedged against a dead backend can throw continuously; cap the
    // noise so one broken page cannot flood Sentry (or the /events/error relay) from one tab.
    if (!underRateLimit()) {
        return;
    }
    isReporting = true;
    try {
        logError(error, {
            tags: { source },
            context: { page: { path: globalThis.location.pathname, ...extraContext } },
        });
    } catch {
        // The safety net must never itself take the page down.
    } finally {
        isReporting = false;
    }
}

/**
 * Window-level listeners. Called at module scope in vue.js, before the app mounts, so errors
 * during boot are covered too. Also owns the ResizeObserver silencing that used to live inline
 * in vue.js: those events are a benign browser quirk (surfaced by Fullstory), not errors.
 */
export function installWindowErrorReporting(): void {
    globalThis.addEventListener("error", (event) => {
        if (event.message?.includes("ResizeObserver loop")) {
            event.preventDefault();
            return;
        }
        report(event.error ?? new Error(event.message || "Unknown window error"), "window_onerror");
    });
    globalThis.addEventListener("unhandledrejection", (event) => {
        report(event.reason ?? new Error("Unhandled promise rejection"), "unhandled_rejection");
    });
}

/**
 * Vue's own error channel — errors thrown in components, watchers, and lifecycle hooks never
 * reach window.onerror, only this handler. Still logs to the console so local dev keeps the
 * behavior Vue's default (re-throw) provided.
 */
export function installVueErrorHandler(app: App): void {
    app.config.errorHandler = (error, _instance, info) => {
        console.error(error);
        report(error, "vue_error_handler", { vue_info: info });
    };
}
